import { useState, useEffect, useRef, useCallback } from 'react';
import { useSession } from '../contexts/SessionContext';
import { Chunk } from '../types';
import apiService from '../services/api';
import './ExportPage.css';

const ExportPage = () => {
  const { getSelectedChunks, currentSessionId, userId } = useSession();
  const [exportFormat, setExportFormat] = useState('json');
  const [selectedChunks, setSelectedChunks] = useState<Chunk[]>([]);
  const [allChunks, setAllChunks] = useState<Chunk[]>([]);
  const [allChunksLoaded, setAllChunksLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAllChunks, setLoadingAllChunks] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [hasSeenIndicator, setHasSeenIndicator] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  // Check if there's more content to scroll (only if user hasn't seen indicator before)
  const checkScrollable = useCallback(() => {
    if (hasSeenIndicator) {
      setShowScrollIndicator(false);
      return;
    }
    if (pageRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = pageRef.current;
      // Show indicator if there's more than 1px of content below
      const hasMoreContent = scrollHeight - scrollTop - clientHeight > 1;
      setShowScrollIndicator(hasMoreContent);

      // Once user starts scrolling, mark as seen
      if (scrollTop > 50) {
        setHasSeenIndicator(true);
        setShowScrollIndicator(false);
      }
    }
  }, [hasSeenIndicator]);

  // Check scrollability on mount and when content changes
  useEffect(() => {
    checkScrollable();
    // Re-check after a short delay to account for dynamic content loading
    const timer = setTimeout(checkScrollable, 500);
    return () => clearTimeout(timer);
  }, [checkScrollable, selectedChunks, allChunks, loading, loadingAllChunks]);

  // Add scroll listener
  useEffect(() => {
    const handleScroll = () => checkScrollable();
    const currentRef = pageRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
      return () => currentRef.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [checkScrollable]);

  // Fetch selected chunks when component mounts or session changes
  useEffect(() => {
    const fetchSelectedChunks = async () => {
      setLoading(true);
      try {
        const chunks = await getSelectedChunks();
        setSelectedChunks(chunks);
      } catch (error) {
        console.error('Error fetching selected chunks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSelectedChunks();
  }, [currentSessionId, getSelectedChunks]);

  // Function to fetch all chunks
  const fetchAllChunks = async () => {
    setLoadingAllChunks(true);
    try {
      const response = await apiService.getLatestChunks(10000, userId);
      setAllChunks(response.data.matches);
      setAllChunksLoaded(true);
    } catch (error) {
      console.error('Error fetching all chunks:', error);
      alert('Failed to load all chunks. Please try again.');
    } finally {
      setLoadingAllChunks(false);
    }
  };

  const exportSelectedChunks = () => {
    exportChunks(selectedChunks, 'selected-chunks');
  };

  const exportAllChunks = async () => {
    // Fetch all chunks first if not already loaded
    if (!allChunksLoaded) {
      await fetchAllChunks();
      // After fetching, allChunks state will be updated, but we need to wait for it
      // So we'll export in a separate call. For now, just notify the user.
      alert('All chunks loaded. Click "Export All Chunks" again to download.');
      return;
    }
    exportChunks(allChunks, 'all-chunks');
  };

  const exportChunks = (chunks: Chunk[], filenamePrefix: string) => {
    if (chunks.length === 0) {
      alert('No chunks to export');
      return;
    }

    let content: string = '';
    let filename: string = '';
    let mimeType: string = '';

    if (exportFormat === 'json') {
      content = JSON.stringify(chunks, null, 2);
      filename = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    } else if (exportFormat === 'txt') {
      content = chunks.map((chunk, index) =>
        `Chunk ${index + 1}:\n` +
        `ID: ${chunk.id}\n` +
        `Score: ${chunk.score}\n` +
        `Category: ${chunk.metadata?.source || 'Unknown'}\n` +
        `Text: ${chunk.metadata?.text || 'No text available'}\n` +
        `${'='.repeat(50)}\n`
      ).join('\n');
      filename = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.txt`;
      mimeType = 'text/plain';
    } else if (exportFormat === 'csv') {
      const headers = 'ID,Score,Source,Text\n';
      const rows = chunks.map(chunk =>
        `"${chunk.id}","${chunk.score}","${chunk.metadata?.source || ''}","${(chunk.metadata?.text || '').replace(/"/g, '""')}"`
      ).join('\n');
      content = headers + rows;
      filename = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    } else {
      // Default to JSON if format is unknown
      content = JSON.stringify(chunks, null, 2);
      filename = `${filenamePrefix}-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    }

    // Create and trigger download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (selectedChunks.length === 0) {
      alert('No chunks selected to copy');
      return;
    }

    const content = selectedChunks.map(chunk => chunk.metadata?.text || '').join('\n\n---\n\n');

    try {
      await navigator.clipboard.writeText(content);
      alert('Selected chunks copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="export-page">
        <div className="empty-message">Loading selected chunks...</div>
      </div>
    );
  }

  return (
    <div className="export-page" ref={pageRef}>
      <div className="stats-section">
        <h2>Statistics</h2>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Selected Chunks:</span>
            <span className="stat-value">{selectedChunks.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Characters (Selected):</span>
            <span className="stat-value">
              {selectedChunks.reduce((total, chunk) =>
                total + (chunk.metadata?.text?.length || 0), 0
              ).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">All Chunks:</span>
            <span className="stat-value">
              {loadingAllChunks ? 'Loading...' : (allChunksLoaded ? allChunks.length : '(Click "Load All" to see)')}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Characters (All):</span>
            <span className="stat-value">
              {allChunks.length > 0
                ? allChunks.reduce((total, chunk) =>
                    total + (chunk.metadata?.text?.length || 0), 0
                  ).toLocaleString()
                : '—'
              }
            </span>
          </div>
        </div>
      </div>

      <div className="export-section">
        <h2>Export Selected Chunks</h2>

        <div className="export-controls">
          <div className="format-selection">
            <label htmlFor="export-format">Export Format:</label>
            <select
              id="export-format"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="format-dropdown"
            >
              <option value="json">JSON</option>
              <option value="txt">Text</option>
              <option value="csv">CSV</option>
            </select>
          </div>

          <div className="export-buttons">
            <button
              onClick={exportSelectedChunks}
              disabled={selectedChunks.length === 0}
              className="export-button primary"
            >
              📥 Export Selected as {exportFormat.toUpperCase()}
            </button>

            <button
              onClick={copyToClipboard}
              disabled={selectedChunks.length === 0}
              className="export-button"
            >
              📋 Copy Text to Clipboard
            </button>
          </div>
        </div>

        {selectedChunks.length === 0 && (
          <p className="no-data-message">
            No chunks selected for export. Go to the Search page to select chunks.
          </p>
        )}
      </div>

      <div className="export-section">
        <h2>Export All Chunks</h2>

        <div className="export-controls">
          <div className="export-buttons">
            <button
              onClick={fetchAllChunks}
              disabled={loadingAllChunks}
              className="export-button"
            >
              {loadingAllChunks ? '⏳ Loading...' : '📂 Load All Chunks'}
            </button>

            <button
              onClick={exportAllChunks}
              disabled={!allChunksLoaded || loadingAllChunks}
              className="export-button primary"
            >
              📥 Export All as {exportFormat.toUpperCase()}
            </button>
          </div>
        </div>

        <p className="info-message">
          {allChunksLoaded
            ? `${allChunks.length} chunk${allChunks.length !== 1 ? 's' : ''} loaded and ready to export.`
            : 'Click "Load All Chunks" to fetch all your chunks from the database.'
          }
        </p>
      </div>

      {/* Scroll indicator */}
      {showScrollIndicator && (
        <div className="scroll-indicator">
          <span className="scroll-arrow">↓</span>
        </div>
      )}
    </div>
  );
};

export default ExportPage;
